import { isLoading } from "apollo/utils";
import {
  FILE_TABLE_ACTION_SEE_DETAILS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { FileState, QueryProgress } from "core/types";
import { Button, Modal, ModalBody, ModalContent, ModalHeader } from "ds/ui";
import { GeneralListAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { PdfViewer, PdfViewerFile } from "dsl/atoms/PDFViewer";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "translations";
import { useCreateFileUrl } from "../useDownloadFile";
import { FileFeatureData } from "../useFilesFeature";

export const FileDetailsModal = ({
  action,
  externalId,
}: {
  externalId: string | undefined;
  action: GeneralListAction<FileFeatureData> | undefined;
}) => {
  const translations = useTranslations();
  const [selectedFile, setSelectedFile] = useState<PdfViewerFile | null>(null);
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const toast = useToast();
  const createFileUrl = useCreateFileUrl({
    externalId: externalId || "",
  });

  useEffect(() => {
    if (action?.type !== FILE_TABLE_ACTION_SEE_DETAILS) return;

    (async () => {
      try {
        setProgress(QUERY_PROGRESS_PENDING);
        const file = action.data as FileState;
        const props = await getFileNameAndIsNewVersionAvailable({
          canAccessPatientData: true,
          file,
        });
        const url = await createFileUrl(file);
        if (!url) {
          throw new Error("[FileDetailsModal] File URL is undefined");
        }

        setSelectedFile({
          fileName: props.value as string,
          id: file.id,
          uploadedAt: file.updated_at!,
          url: url,
        });
        setProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        setProgress(QUERY_PROGRESS_FAILED);
        toast({
          color: "danger",
          message: translations.general.error,
        });
        console.error(error);
        setSelectedFile(null);
      }
    })();
  }, [action]);

  if (!selectedFile) return null;

  const isLoadingPdf = isLoading(progress);

  return (
    <Modal hideCloseButton isOpen={!!selectedFile}>
      <ModalContent className="md:max-h-[85vh] md:w-[90vw] lg:h-[864px] lg:max-h-[864px] flex max-h-[90vh] w-[95vw] max-w-[800px] flex-col">
        <ModalHeader className="flex w-full justify-between p-4">
          <h1 className="text-heading-lg font-bold">
            {selectedFile.fileName ||
              translations.fileSection.fileListComponent.fileName}
          </h1>
          <Button
            aria-label={translations.actions.close}
            color="black"
            id="close-file-details-modal"
            isIconOnly
            size="sm"
            startContent={<XIcon />}
            variant="light"
            onPress={() => setSelectedFile(null)}
          />
        </ModalHeader>
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex-1 overflow-y-auto p-4">
          <PdfViewer isLoadingPdf={isLoadingPdf} selectedFile={selectedFile} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
